import { useState } from "react";
import { Bell, Check, CheckCheck, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { apiRequest } from "../../lib/api";
import { formatDate, formatRelative } from "../../lib/format";
import { useAsync } from "../../hooks/useAsync";
import { notifyInboxChanged, useNotificationRefresh } from "../../hooks/useNotificationRefresh";
import { useToast } from "../../context/ToastContext";

type Notification = {
  id: string; type: string; title: string; body: string; action_url: string | null;
  read_at: string | null; created_at: string; emailed_at: string | null; pushed_at: string | null;
};
type Inbox = { notifications: Notification[]; nextCursor: string | null };
const labels: Record<string, string> = {
  "ticket.created": "New ticket", "ticket.assigned": "Assigned to you",
  "ticket.reply_added": "Customer reply", "ticket.sla_warning": "SLA reminder",
};

export function AgentNotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [busy, setBusy] = useState<string | null>(null);
  const before = cursors[cursors.length - 1];
  const result = useAsync((signal) => apiRequest<Inbox>(
    `/agent/notifications?unread=${unreadOnly}${before ? `&before=${encodeURIComponent(before)}` : ""}`, { signal }),
  [unreadOnly, before], { keepPreviousData: true });
  useNotificationRefresh(result.refresh);
  const { showToast } = useToast();

  async function markRead(id?: string) {
    setBusy(id || "all");
    try {
      await apiRequest(id ? `/agent/notifications/${id}/read` : "/agent/notifications/read-all", { method: "POST" });
      notifyInboxChanged();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not mark notifications as read.", "error");
    } finally { setBusy(null); }
  }
  const updating = result.loading || result.refreshing;
  return <div className="notifications-page">
    <header className="notifications-heading">
      <div><h1>Notifications</h1><p>Ticket updates and reminders. History is available even when device push is turned off.</p></div>
      <Link className="button button--secondary" to="/agent/settings">Notification settings</Link>
    </header>
    <section className="card notification-inbox" aria-label="Notification history">
      <div className="notification-inbox__toolbar">
        <label><input type="checkbox" checked={unreadOnly} disabled={updating}
          onChange={(event) => { setUnreadOnly(event.target.checked); setCursors([null]); }} /> Unread only</label>
        <div>
          <button className="button button--secondary" type="button" disabled={updating} onClick={() => {
            if (before) setCursors([null]); else void result.refresh();
          }}><RefreshCw size={16} aria-hidden="true" /> Refresh</button>
          <button className="button button--secondary" type="button" disabled={!!busy}
            onClick={() => void markRead()}><CheckCheck size={16} aria-hidden="true" /> Mark all read</button>
        </div>
      </div>
      {result.error && <p role="alert" className="notification-inbox__notice">{result.error} <button type="button" onClick={() => void result.refresh()}>Retry</button></p>}
      {result.loading && <p role="status" className="notification-inbox__notice">Loading notifications...</p>}
      <div aria-busy={updating}>
        {result.data?.notifications.map((item) => {
          const ticketUrl = item.action_url && /^\/agent\/tickets\/[0-9a-f-]+$/i.test(item.action_url) ? item.action_url : null;
          return <article key={item.id} className={`notification-item${item.read_at ? "" : " notification-item--unread"}`}>
            <Bell className="notification-item__icon" size={19} aria-hidden="true" />
            <div className="notification-item__content">
              <div className="notification-item__meta"><span>{labels[item.type] || "Ticket update"}{!item.read_at && " · Unread"}</span>
                <time dateTime={item.created_at} title={formatDate(item.created_at)}>{formatRelative(item.created_at)}</time></div>
              <h2>{ticketUrl ? <Link to={ticketUrl} onClick={() => { if (!item.read_at) void markRead(item.id); }}>{item.title}</Link> : item.title}</h2>
              <p>{item.body}</p>
              {(item.emailed_at || item.pushed_at) && <small>
                {item.emailed_at && `Email accepted · ${formatDate(item.emailed_at)}`}
                {item.emailed_at && item.pushed_at && " | "}
                {item.pushed_at && `Push service accepted · ${formatDate(item.pushed_at)}`}
              </small>}
            </div>
            {!item.read_at && <button type="button" className="icon-button" aria-label={`Mark ${item.title} as read`}
              title="Mark as read" disabled={!!busy} onClick={() => void markRead(item.id)}><Check size={18} aria-hidden="true" /></button>}
          </article>;
        })}
        {result.data?.notifications.length === 0 && <p className="notification-inbox__notice">{unreadOnly ? "You're all caught up." : "No notifications yet."}</p>}
      </div>
      <footer className="notification-inbox__pagination">
        <button className="button button--secondary" type="button" disabled={updating || cursors.length === 1}
          onClick={() => setCursors((values) => values.slice(0, -1))}>Newer</button>
        <span>{result.refreshing ? "Updating..." : `Page ${cursors.length}`}</span>
        <button className="button button--secondary" type="button" disabled={updating || !result.data?.nextCursor}
          onClick={() => { if (result.data?.nextCursor) setCursors((values) => [...values, result.data!.nextCursor]); }}>Older</button>
      </footer>
    </section>
  </div>;
}
